<script setup>
  import { provide, ref, watch, onMounted, onUnmounted, nextTick } from "vue";
  import { useEventBus } from "@vueuse/core";
  import { useRoute } from "vue-router";
  import { storeToRefs } from "pinia";
  import TwoPanePage from "@/templates/TwoPanePage.vue";
  import CardsPane from "@/components/Survey/Components/CardsList/CardsPane.vue";
  import TestPreview from "./TestPreview.vue";
  import { useSurveySimulation } from "@utils/composables/surveySimulation";
  import { useScrollToCard } from "@utils/composables/useScrollToCard";
  import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";

  const props = defineProps({
    survey: { type: Object, required: true },
    cards: { type: Array, required: true },
    questionCount: { type: Number, required: true },
    cardSubTypesInfos: { type: Object, required: true },
    isCardFilteredOut: { type: Function, required: true },
    isFilterActive: { type: Boolean, required: true },
  });

  const route = useRoute();
  const surveyLocalesStore = useSurveyLocalesStore();
  const { localeModel } = storeToRefs(surveyLocalesStore);

  const surveyCardsStore = useSurveyCardsStore();
  const { selectCurrentCard, setActivePane } = surveyCardsStore;

  const simulationEventBus = useEventBus("simulation");
  provide("simulationEventBus", simulationEventBus);

  const {
    currentCard,
    currentLocaleSimu,
    handleChatMessage,
    reloadChat,
    goToCard,
  } = useSurveySimulation(props.survey, route.query);

  // Watch for locale changes from header and sync with simulation
  watch(localeModel, (newLocale) => {
    if (newLocale && newLocale !== currentLocaleSimu.value) {
      currentLocaleSimu.value = newLocale;
      reloadChat();
    }
  });

  const goToEdit = async ({ token }) => {
    setActivePane("Edit");
    await nextTick();
    selectCurrentCard({ token });
  };

  simulationEventBus.on((eventDescriptor) => {
    const { command, ...event } = eventDescriptor;
    const actions = { select: goToCard, goToEdit };
    actions[command]?.(event);
  });

  const cardsPane = ref(null);
  const { scrollToCard } = useScrollToCard(cardsPane, currentCard);

  watch(currentCard, () => {
    scrollToCard();
  });

  onMounted(() => {
    window.addEventListener("message", handleChatMessage);
  });

  onUnmounted(() => {
    window.removeEventListener("message", handleChatMessage);
  });
</script>

<template>
  <TwoPanePage
    :span-left="4"
    :span-right="8"
    data-cy="survey-test-page">
    <template #left>
      <CardsPane
        ref="cardsPane"
        :card-sub-types-infos="cardSubTypesInfos"
        :cards="cards"
        event-bus-name="simulationEventBus"
        :is-card-filtered-out="isCardFilteredOut"
        :is-filter-active="isFilterActive"
        :is-footer-hidden="true"
        :is-list-read-only="true"
        :is-test-mode="true"
        :question-count="questionCount"
        :selected-card-token="currentCard" />
    </template>
    <template #right>
      <TestPreview :survey="survey" />
    </template>
  </TwoPanePage>
</template>
